import { useState } from 'react'
import { Check, Copy, LogIn, Users } from 'lucide-react'
import { User } from "@/types"

interface Props {
    users: User[]
}

export default function UsersTable({ users }: Props) {
    const [copiedTokens, setCopiedTokens] = useState<Set<string>>(new Set())

    const loginAsUser = (user: User) => {
        const magicUrl = `/magic/${user.magic_token}`
        window.open(magicUrl, '_blank', 'noopener,noreferrer')
    }

    const copyMagicLink = async (user: User) => {
        const magicUrl = `${window.location.origin}/magic/${user.magic_token}`
        try {
            await navigator.clipboard.writeText(magicUrl)
            setCopiedTokens(prev => new Set(prev).add(user.magic_token))
            setTimeout(() => {
                setCopiedTokens(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(user.magic_token)
                    return newSet
                })
            }, 2000)
        } catch (err) {
            console.error('Failed to copy magic link:', err)
        }
    }

    return (
        <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Users & Magic Login Links</h3>
                <p className="mt-1 text-sm text-gray-500">
                    Access user accounts directly with magic login links
                </p>
            </div>

            {users.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                    <Users className="mx-auto h-12 w-12 text-gray-400 mb-4"/>
                    <p>No users found for this organization</p>
                </div>
            ) : (
                <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Session
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Member Since
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.last_session || 'Never'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.created_at}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end space-x-2">
                                        <button
                                            onClick={() => copyMagicLink(user)}
                                            className="text-gray-600 hover:text-gray-700 p-1 rounded"
                                            title="Copy magic login link"
                                        >
                                            {copiedTokens.has(user.magic_token) ? (
                                                <Check className="h-4 w-4 text-green-600"/>
                                            ) : (
                                                <Copy className="h-4 w-4"/>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => loginAsUser(user)}
                                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            title={`Login as ${user.name}`}
                                        >
                                            <LogIn className="h-3 w-3 mr-1"/>
                                            Login
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}